import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useProfileEditData } from '@/hooks/useProfileEditData';
import { useNewVerification } from '@/hooks/useNewVerification';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { debugLogger } from '@/utils/debugLogger';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Camera, Edit3, Check, X, Shield, ShieldCheck, Share, Eye, PowerOff, ShieldAlert, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import EditableAppearanceSection from '@/components/profile-edit/EditableAppearanceSection';
import EditableLifestyleSection from '@/components/profile-edit/EditableLifestyleSection';
import EditableRelationshipsSection from '@/components/profile-edit/EditableRelationshipsSection';
import EditableLocationSection from '@/components/profile-edit/EditableLocationSection';
import { BioInput } from '@/components/shared/BioInput';
import { ImprovedEducationAutocomplete } from '@/components/onboarding/ImprovedEducationAutocomplete';
import { ImprovedOccupationAutocomplete } from '@/components/onboarding/ImprovedOccupationAutocomplete';
import { PhotoManagementModal } from '@/components/profile-edit/PhotoManagementModal';
import { BioEditModal } from '@/components/profile-edit/BioEditModal';
import { BioTopicsDisplayCard } from '@/components/profile-edit/BioTopicsDisplayCard';
import { ProfilePhotoCarousel } from '@/components/profile/ProfilePhotoCarousel';
import { EmptyPhotosPlaceholder } from '@/components/profile-edit/EmptyPhotosPlaceholder';
import { businessRulesEngine } from '@/core/BusinessRulesEngine';
import { AlertTriangle } from 'lucide-react';
import ProfileEditHeader from '@/components/profile/ProfileEditHeader';

const ProfileEdit = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { localData, updateLocalData, syncWithAuthContext, loading, profileData } = useProfileEditData();
  
  const [editingField, setEditingField] = useState<string | null>(null);
  const [formData, setFormData] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [isBioModalOpen, setIsBioModalOpen] = useState(false);
  const [isDangerZoneOpen, setIsDangerZoneOpen] = useState(false);

  const { verification, getCurrentVerification } = useNewVerification();

  // Refs para scroll automático
  const photosRef = useRef<HTMLDivElement>(null);
  const basicInfoRef = useRef<HTMLDivElement>(null);
  const appearanceRef = useRef<HTMLDivElement>(null);
  const lifestyleRef = useRef<HTMLDivElement>(null);
  const relationshipsRef = useRef<HTMLDivElement>(null);
  
  // Estado para controlar qual seção está sendo destacada
  const [highlightedSection, setHighlightedSection] = useState<string | null>(null);

  // Initialize form data when local data changes
  useEffect(() => {
    if (localData) {
      setFormData({ ...localData });
    }
  }, [localData]);

  // Fetch verification data
  useEffect(() => {
    if (user) {
      getCurrentVerification();
    }
  }, [user, getCurrentVerification]);

  const handleEdit = (field: string) => {
    if (field === 'bio') {
      setIsBioModalOpen(true);
    } else {
      setEditingField(field);
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    if (localData) {
      setFormData({ ...localData });
    }
  };

  const handleSave = async (field: string) => {
    if (!user?.id || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const updateData = { [field]: formData[field] };
      
      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);
      
      if (error) throw error;

      // Update local data
      updateLocalData(field, formData[field]);
      
      // Sync with auth context
      await syncWithAuthContext(updateData);
      
      setEditingField(null);
    } catch (error) {
      debugLogger.error('Error updating profile:', error);
      toast.error('Erro ao atualizar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Auto-save para campos simples (occupation, education)
  const handleAutoSave = async (field: string, value: string) => {
    if (!user?.id || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Atualizar estado local imediatamente
      setFormData(prev => ({ ...prev, [field]: value }));
      
      // Salvar no banco
      const { error } = await supabase
        .from('profiles')
        .update({ [field]: value })
        .eq('id', user.id);
      
      if (error) throw error;

      // Update local data
      updateLocalData(field, value);
      
      // Sync with auth context
      await syncWithAuthContext({ [field]: value });
      
      // Fechar modo de edição
      setEditingField(null);
      
      toast.success('Salvo', { duration: 1500 });
    } catch (error) {
      debugLogger.error('ProfileEdit', 'Error auto-saving field:', field, error);
      toast.error('Erro ao salvar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBioSave = async (
    bio: string, 
    aiData?: { 
      topics?: string[],
      score?: number 
    }
  ) => {
    if (!user?.id) return;

    const updateData: any = { bio };
    
    // Salvar apenas campos simplificados que existem no banco
    if (aiData) {
      if (aiData.topics && aiData.topics.length > 0) {
        updateData.bio_topics = aiData.topics;
        updateData.bio_score = aiData.score ?? 0;
        updateData.bio_last_analyzed = new Date().toISOString();
      }
    }
    
    const { error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', user.id);
    
    if (error) throw error;

    // Update local data com campos corretos
    setFormData(prev => ({ ...prev, ...updateData }));
    updateLocalData('bio', bio);
    if (aiData?.topics) {
      updateLocalData('bio_topics', aiData.topics);
      updateLocalData('bio_score', aiData.score);
      updateLocalData('bio_last_analyzed', updateData.bio_last_analyzed);
    }
    
    // Sync with auth context
    await syncWithAuthContext(updateData);
  };

  const handleViewProfile = () => {
    if (formData.username) {
      navigate(`/main/user/${formData.username}`);
    }
  };

  const handleShareProfile = async () => {
    if (navigator.share && formData.username) {
      try {
        await navigator.share({
          title: 'Meu perfil',
          text: `Confira meu perfil!`,
          url: `${window.location.origin}/main/user/${formData.username}`,
        });
      } catch (error) {
        // Fallback para clipboard
        navigator.clipboard.writeText(`${window.location.origin}/main/user/${formData.username}`);
      }
    } else if (formData.username) {
      navigator.clipboard.writeText(`${window.location.origin}/main/user/${formData.username}`);
    }
  };

  const handlePhotosChange = async (photos: string[]) => {
    if (!user?.id) return;

    // Update local state first
    setFormData(prev => ({ ...prev, profile_photos: photos }));
    updateLocalData('profile_photos', photos);

    try {
      // Auto-save to database
      const { error } = await supabase
        .from('profiles')
        .update({ profile_photos: photos })
        .eq('id', user.id);

      if (error) throw error;

      // Sync with auth context
      await syncWithAuthContext({ profile_photos: photos });
      
      toast.success('Fotos atualizadas automaticamente');
      debugLogger.debug('Photos auto-saved successfully', { photos });
    } catch (error) {
      debugLogger.error('Error auto-saving photos:', error);
      toast.error('Erro ao salvar fotos automaticamente');
    }
  };

  const handleMainPhotoChange = async (photo: string) => {
    if (!user?.id) return;

    // Update local state first
    setFormData(prev => ({ ...prev, main_photo_url: photo }));
    updateLocalData('main_photo_url', photo);

    try {
      // Auto-save to database
      const { error } = await supabase
        .from('profiles')
        .update({ main_photo_url: photo })
        .eq('id', user.id);

      if (error) throw error;

      // Sync with auth context
      await syncWithAuthContext({ main_photo_url: photo });
      
      toast.success('Foto principal atualizada automaticamente');
      debugLogger.debug('Main photo auto-saved successfully', { photo });
    } catch (error) {
      debugLogger.error('Error auto-saving main photo:', error);
      toast.error('Erro ao salvar foto principal automaticamente');
    }
  };

  // Removed: handleInterestsUpdate - não mais necessário na v3 simplificada

  const handleDeactivateAccount = async () => {
    if (!user?.id) return;

    try {
      toast.loading('Desativando conta...');
      debugLogger.debug('account', 'Iniciando desativação da conta para usuário:', user.id);
      
      // Atualizar perfil para desativado
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          hidden: true,
          deactivated_at: new Date().toISOString(),
          is_online: false
        })
        .eq('id', user.id);
      
      if (profileError) {
        console.error('Erro ao desativar perfil:', profileError);
        throw new Error('Falha ao desativar conta');
      }
      
      debugLogger.debug('account', 'Conta desativada com sucesso');

      // Fazer logout
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        console.error('Erro ao fazer logout:', signOutError);
      }

      toast.dismiss();
      toast.success('Conta desativada. Você tem 30 dias para reativar fazendo login.');
      
      // Redirecionar para página de autenticação
      navigate('/auth');
      
    } catch (error) {
      debugLogger.error('Erro ao desativar conta:', error);
      console.error('Erro detalhado:', error);
      toast.dismiss();
      toast.error(error instanceof Error ? error.message : 'Erro ao desativar conta. Tente novamente.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-luxury-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!formData || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-white text-lg mb-4">Erro ao carregar perfil</p>
          <Button onClick={() => navigate('/main/feed')} className="bg-luxury-red hover:bg-luxury-red/90">
            Voltar para o Feed
          </Button>
        </div>
      </div>
    );
  }

  const EditableField = ({ 
    field, 
    label, 
    value, 
    multiline = false, 
    placeholder = "",
    maxLength = undefined 
  }: { 
    field: string; 
    label: string; 
    value: string; 
    multiline?: boolean; 
    placeholder?: string;
    maxLength?: number;
  }) => {
    const isEditing = editingField === field;
    
    if (isEditing) {
      // Para o campo bio, usar o BioInput com IA
      if (field === 'bio') {
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-white/80">{label}</label>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handleSave(field)}
                  disabled={isSubmitting}
                  size="sm"
                  className="h-8 px-3 bg-luxury-red hover:bg-luxury-red/90"
                >
                  <Check className="h-3 w-3" />
                </Button>
                <Button
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  size="sm"
                  variant="ghost"
                  className="h-8 px-3 text-white/60 hover:text-white hover:bg-white/10"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <BioInput
              value={formData[field] || ''}
              onChange={(value) => handleInputChange(field, value)}
              placeholder={placeholder}
              maxLength={maxLength}
              showQualityIndicator={true}
              occupation={formData.occupation}
              education={formData.education}
            />
          </div>
        );
      }

      // Para o campo occupation, usar o OccupationAutocomplete com auto-save
      if (field === 'occupation') {
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-white/80">{label}</label>
              {isSubmitting && (
                <span className="text-xs text-white/40">Salvando...</span>
              )}
            </div>
            <ImprovedOccupationAutocomplete
              value={formData[field] || ''}
              onChange={(value) => handleAutoSave(field, value)}
              userRole={formData.role || "sponsor"}
            />
          </div>
        );
      }

      // Para o campo education, usar o EducationAutocomplete com auto-save
      if (field === 'education') {
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-white/80">{label}</label>
              {isSubmitting && (
                <span className="text-xs text-white/40">Salvando...</span>
              )}
            </div>
            <ImprovedEducationAutocomplete
              value={formData[field] || ''}
              onChange={(value) => handleAutoSave(field, value)}
            />
          </div>
        );
      }

      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-white/80">{label}</label>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => handleSave(field)}
                disabled={isSubmitting}
                size="sm"
                className="h-8 px-3 bg-luxury-red hover:bg-luxury-red/90"
              >
                <Check className="h-3 w-3" />
              </Button>
              <Button
                onClick={handleCancel}
                disabled={isSubmitting}
                size="sm"
                variant="ghost"
                className="h-8 px-3 text-white/60 hover:text-white hover:bg-white/10"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
          {multiline ? (
            <Textarea
              value={formData[field] || ''}
              onChange={(e) => handleInputChange(field, e.target.value)}
              placeholder={placeholder}
              maxLength={maxLength}
              className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-luxury-gold/50 resize-none"
              rows={3}
            />
          ) : (
            <Input
              value={formData[field] || ''}
              onChange={(e) => handleInputChange(field, e.target.value)}
              placeholder={placeholder}
              maxLength={maxLength}
              className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-luxury-gold/50"
            />
          )}
          {maxLength && field !== 'bio' && (
            <div className="text-xs text-white/40 text-right">
              {(formData[field] || '').length}/{maxLength}
            </div>
          )}
        </div>
      );
    }

    return (
      <div 
        onClick={() => handleEdit(field)}
        className="group cursor-pointer hover:bg-white/5 rounded-lg p-3 -m-3 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <label className="text-sm font-medium text-white/80 block mb-1">{label}</label>
            {value ? (
              <p className="text-white text-base leading-relaxed break-words">{value}</p>
            ) : (
              <p className="text-white/40 text-sm italic">{placeholder}</p>
            )}
          </div>
          <Edit3 className="h-4 w-4 text-white/40 group-hover:text-white/60 transition-colors flex-shrink-0 ml-3" />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Mobile Header */}
      <ProfileEditHeader />

      <div className="px-4 lg:px-8 py-6 pb-24 md:pb-6 lg:max-w-7xl lg:mx-auto space-y-6">

        {/* Warning: Profile Incomplete */}
        {(() => {
          const photoCount = formData.profile_photos?.length || 0;
          const hasMinimumPhotos = photoCount >= 3;
          const isProfileComplete = hasMinimumPhotos && formData.bio && formData.occupation && formData.education && 
                                   formData.height && formData.body_type && formData.hair_color && 
                                   formData.eye_color && formData.relationship_type && formData.marital_status;
          
          if (!isProfileComplete) {
            const missingFields = [];
            if (!hasMinimumPhotos) {
              const photosNeeded = 3 - photoCount;
              missingFields.push(`fotos (faltam ${photosNeeded})`);
            }
            if (!formData.bio) missingFields.push('bio');
            if (!formData.occupation) missingFields.push('ocupação');
            if (!formData.education) missingFields.push('educação');
            if (!formData.height) missingFields.push('altura');
            if (!formData.body_type) missingFields.push('tipo físico');
            if (!formData.hair_color) missingFields.push('cor do cabelo');
            if (!formData.eye_color) missingFields.push('cor dos olhos');
            if (!formData.relationship_type) missingFields.push('tipo de relacionamento');
            if (!formData.marital_status) missingFields.push('estado civil');
            
            const fieldRefMap: Record<string, React.RefObject<HTMLDivElement>> = {
              'fotos': photosRef,
              'bio': basicInfoRef,
              'ocupação': basicInfoRef,
              'educação': basicInfoRef,
              'altura': appearanceRef,
              'tipo físico': appearanceRef,
              'cor do cabelo': appearanceRef,
              'cor dos olhos': appearanceRef,
              'tipo de relacionamento': relationshipsRef,
              'estado civil': relationshipsRef,
            };

            const scrollToFirstMissing = () => {
              if (missingFields.length > 0) {
                const firstMissing = missingFields[0];
                const baseField = firstMissing.split(' (')[0];
                const ref = fieldRefMap[baseField];
                
                if (ref?.current) {
                  ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  
                  // Ativar highlight visual
                  setHighlightedSection(baseField);
                  
                  // Remover highlight após 3 segundos
                  setTimeout(() => setHighlightedSection(null), 3000);
                }
              }
            };
            
            return (
              <Card className="bg-orange-500/10 border-orange-500/30">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-orange-500">
                        Perfil Incompleto
                      </p>
                      <p className="text-xs text-white/70 mt-1">
                        Complete as informações do seu perfil para ter acesso total à plataforma. 
                        Campos faltando: {missingFields.slice(0, 3).join(', ')}
                        {missingFields.length > 3 && ` e mais ${missingFields.length - 3}`}
                      </p>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={scrollToFirstMissing}
                        className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 mt-2 h-8 px-3"
                      >
                        Completar agora →
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }
          return null;
        })()}

        {/* Verification Status Badge */}
        {(() => {
          if (!verification) return null;
          
          const isFullyVerified = formData.verification_level === 'full' || verification.selfie_verified === true;
          const hasSubmittedSelfie = Boolean(verification.selfie_photo_url);
          const isPending = hasSubmittedSelfie && 
                           (verification.status === 'pending' || verification.status === 'waiting' || 
                            (verification.selfie_verified === false && !verification.ai_analysis));
          const isRejected = verification.ai_analysis && verification.selfie_verified === false;
          const isNotStarted = !hasSubmittedSelfie && !isFullyVerified && !isRejected;
          
          // Fully verified - green badge
          if (isFullyVerified) {
            return (
              <Card className="bg-green-500/10 border-green-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-500">
                        Perfil Verificado
                      </p>
                      <p className="text-xs text-white/70 mt-1">
                        Sua verificação foi aprovada. Seu perfil tem maior visibilidade.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }
          
          // Pending analysis - yellow badge (only if selfie was submitted)
          if (isPending) {
            return (
              <Card className="bg-yellow-500/10 border-yellow-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-yellow-500 flex-shrink-0 animate-pulse" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-500">
                        Verificação em Análise
                      </p>
                      <p className="text-xs text-white/70 mt-1">
                        Sua selfie está sendo analisada. Isso pode levar alguns minutos.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }
          
          // Rejected - red badge with action
          if (isRejected) {
            return (
              <Card className="bg-red-500/10 border-red-500/30 cursor-pointer hover:bg-red-500/15 transition-colors"
                    onClick={() => navigate('/main/verify')}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-500">
                        Verificação Reprovada
                      </p>
                      <p className="text-xs text-white/70 mt-1">
                        Sua verificação não foi aprovada. 
                        {verification.ai_analysis?.reason && (
                          <> Motivo: {verification.ai_analysis.reason}</>
                        )}
                        {' '}Toque para tentar novamente.
                      </p>
                    </div>
                    <div className="text-red-500/60">
                      <span className="text-xs">→</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }
          
          // Not started - call to action to verify
          if (isNotStarted) {
            return (
              <Card className="bg-white/5 border-white/10 cursor-pointer hover:bg-white/10 transition-colors"
                    onClick={() => navigate('/main/verify')}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-white/60 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white/80">
                        Verificar Perfil
                      </p>
                      <p className="text-xs text-white/50 mt-1">
                        Aumente sua visibilidade verificando seu perfil. Toque para começar.
                      </p>
                    </div>
                    <span className="text-white/40 text-xs">→</span>
                  </div>
                </CardContent>
              </Card>
            );
          }
          
          return null;
        })()}

        {/* Duplicate verification card removed - see above */}

        {/* Photo Section - SEMPRE renderizada */}
        <div 
          ref={photosRef}
          className={`transition-all duration-300 ${
            highlightedSection === 'fotos' ? 'ring-2 ring-luxury-gold animate-shake' : ''
          }`}
        >
          {(formData.profile_photos?.length || 0) > 0 ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Photo Carousel */}
                <ProfilePhotoCarousel
                  photos={formData.profile_photos || []}
                  userName={formData.username || 'Usuário'}
                  isVerified={formData.verification_level === 'full'}
                />
                
                {/* Right: Bio Topics Management - Desktop only */}
                <div className="relative hidden lg:flex items-center">
                  <BioTopicsDisplayCard
                    topics={formData.bio_topics || []}
                    bioScore={formData.bio_score}
                    lastAnalyzed={formData.bio_last_analyzed}
                    onEditBio={() => setIsBioModalOpen(true)}
                    isEditMode={false}
                  />
                </div>
              </div>

              {/* Fixed Photo Management Button - Outside Carousel */}
              <Button 
                onClick={() => setIsPhotoModalOpen(true)}
                className="w-full bg-luxury-red/90 hover:bg-luxury-red text-white mt-6"
                size="sm"
              >
                <Camera className="h-4 w-4 mr-2" />
                Alterar Fotos
              </Button>
            </>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Empty Photos Placeholder */}
              <EmptyPhotosPlaceholder 
                onAddPhotos={() => setIsPhotoModalOpen(true)}
                isHighlighted={highlightedSection === 'fotos'}
              />
              
              {/* Right: Bio Topics / AI Analysis - Desktop only */}
              <div className="hidden lg:flex items-center">
                <BioTopicsDisplayCard
                  topics={formData.bio_topics || []}
                  bioScore={formData.bio_score}
                  lastAnalyzed={formData.bio_last_analyzed}
                  onEditBio={() => setIsBioModalOpen(true)}
                  isEditMode={false}
                />
              </div>
            </div>
          )}
        </div>

            {/* Bio Topics Display - Mobile only (appears right after "Alterar Fotos") */}
            <div className="lg:hidden">
              <BioTopicsDisplayCard
                topics={formData.bio_topics || []}
                bioScore={formData.bio_score}
                lastAnalyzed={formData.bio_last_analyzed}
                onEditBio={() => setIsBioModalOpen(true)}
                isEditMode={false}
              />
            </div>

            {/* Information Sections: Left large card + Right stacked sections on desktop */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column: Informações Básicas (50% on desktop) */}
          <div 
            ref={basicInfoRef} 
            className={`lg:w-1/2 transition-all duration-300 ${
              highlightedSection === 'bio' || highlightedSection === 'ocupação' || highlightedSection === 'educação'
                ? 'ring-2 ring-luxury-gold animate-shake'
                : ''
            }`}
          >
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-luxury-gold/20 rounded-full flex items-center justify-center">
                    <span className="text-lg">📋</span>
                  </div>
                  <h3 className="text-white font-medium text-lg">Informações Básicas</h3>
                  {formData.verification_level === 'complete' ? (
                    <ShieldCheck className="h-5 w-5 text-luxury-gold" />
                  ) : formData.verification_level === 'full' && (
                    <Shield className="h-5 w-5 text-luxury-gold" />
                  )}
                </div>

                <div className="space-y-4">
                  <EditableField
                    field="bio"
                    label="Sobre você"
                    value={formData.bio || ''}
                    placeholder="Conte um pouco sobre você"
                    multiline
                    maxLength={300}
                  />

                  <EditableField
                    field="occupation"
                    label="Profissão"
                    value={formData.occupation || ''}
                    placeholder="O que você faz profissionalmente?"
                    maxLength={100}
                  />

                  <EditableField
                    field="education"
                    label="Formação"
                    value={formData.education || ''}
                    placeholder="Sua formação acadêmica"
                    maxLength={100}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Stacked sections (50% on desktop) */}
          <div className="lg:w-1/2 space-y-6">
            <div 
              ref={appearanceRef}
              className={`transition-all duration-300 ${
                highlightedSection === 'altura' || highlightedSection === 'tipo físico' || 
                highlightedSection === 'cor do cabelo' || highlightedSection === 'cor dos olhos'
                  ? 'ring-2 ring-luxury-gold animate-shake'
                  : ''
              }`}
            >
              <EditableAppearanceSection
                formData={formData}
                updateLocalData={updateLocalData}
                syncWithAuthContext={syncWithAuthContext}
                userId={user.id}
              />
            </div>

            <div 
              ref={lifestyleRef}
              className={`transition-all duration-300 ${
                highlightedSection === 'fumante' || highlightedSection === 'bebedor'
                  ? 'ring-2 ring-luxury-gold animate-shake'
                  : ''
              }`}
            >
              <EditableLifestyleSection
                formData={formData}
                updateLocalData={updateLocalData}
                syncWithAuthContext={syncWithAuthContext}
                userId={user.id}
              />
            </div>

            <div 
              ref={relationshipsRef}
              className={`transition-all duration-300 ${
                highlightedSection === 'tipo de relacionamento' || highlightedSection === 'estado civil'
                  ? 'ring-2 ring-luxury-gold animate-shake'
                  : ''
              }`}
            >
              <EditableRelationshipsSection
                formData={formData}
                updateLocalData={updateLocalData}
                syncWithAuthContext={syncWithAuthContext}
                userId={user.id}
              />
            </div>

            {/* Location Section */}
            <EditableLocationSection
              formData={formData}
              updateLocalData={updateLocalData}
              syncWithAuthContext={syncWithAuthContext}
              userId={user.id}
            />
          </div>
        </div>

        {/* Danger Zone - Account Deactivation (Collapsible) */}
        <Collapsible
          open={isDangerZoneOpen} 
          onOpenChange={setIsDangerZoneOpen}
          className="border border-white/10 rounded-lg overflow-hidden"
        >
          {/* Trigger - sempre visível */}
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between px-6 py-4 bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
              <span className="text-white/60 text-sm">
                Desativar conta
              </span>
              <ChevronDown 
                className={`w-4 h-4 text-white/40 transition-transform duration-200 ${
                  isDangerZoneOpen ? 'rotate-180' : ''
                }`}
              />
            </div>
          </CollapsibleTrigger>

          {/* Content - expande ao clicar */}
          <CollapsibleContent>
            <div className="bg-white/5 border-t border-white/10">
              <div className="p-6 space-y-4">
                {/* Informações sobre desativação */}
                <div className="space-y-3">
                  <p className="text-white/80 text-sm leading-relaxed">
                    Ao desativar sua conta, seu perfil será ocultado de todos os usuários e você não receberá mais notificações.
                  </p>
                  
                  <div className="bg-black/30 border border-white/20 rounded-md p-3 space-y-2">
                    <p className="text-white/70 text-xs font-medium">
                      ⏱️ Período de recuperação: 30 dias
                    </p>
                    <p className="text-white/60 text-xs">
                      Durante este período, você pode fazer login para reativar sua conta a qualquer momento.
                    </p>
                  </div>

                  <div className="bg-black/30 border border-white/20 rounded-md p-3">
                    <p className="text-white/70 text-xs font-medium">
                      ⚠️ Após 30 dias, sua conta será permanentemente deletada e não poderá ser recuperada.
                    </p>
                  </div>
                </div>

                {/* Botão de ação */}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20"
                    >
                      <PowerOff className="w-4 h-4 mr-2" />
                      Desativar Conta
                    </Button>
                  </AlertDialogTrigger>
                  
                  {/* Modal de confirmação */}
                  <AlertDialogContent 
                    className="bg-black/90 border-white/20 text-white"
                    hasTitle={true}
                  >
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white">
                        Confirmar Desativação da Conta
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-white/70">
                        Revise as informações abaixo antes de confirmar a desativação da sua conta.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="text-white/80 space-y-3">
                      <div><strong>O que acontece ao desativar:</strong></div>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Seu perfil fica invisível para outros usuários</li>
                        <li>Você não aparece em buscas ou feed</li>
                        <li>Suas conversas ficam pausadas</li>
                        <li>Notificações são desativadas</li>
                      </ul>
                      <div className="mt-4">
                        <strong className="text-white/90">Período de Recuperação:</strong>
                      </div>
                      <div>
                        Você tem <strong>30 dias</strong> para fazer login e reativar sua conta. 
                        Após esse período, todos os seus dados serão{' '}
                        <strong className="text-white/90">permanentemente deletados</strong>:
                      </div>
                      <ul className="list-disc list-inside space-y-1 ml-2 text-white/70">
                        <li>Perfil completo e fotos</li>
                        <li>Todas as conversas e mensagens</li>
                        <li>Conexões e matches</li>
                        <li>Conta de usuário</li>
                      </ul>
                      <div className="mt-4">Tem certeza que deseja continuar?</div>
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                        Cancelar
                      </AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDeactivateAccount}
                        className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
                      >
                        Sim, Desativar Minha Conta
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* Photo Management Modal */}
      <PhotoManagementModal
        isOpen={isPhotoModalOpen}
        onOpenChange={setIsPhotoModalOpen}
        photos={formData.profile_photos || []}
        mainPhoto={formData.main_photo_url || ''}
        onPhotosChange={handlePhotosChange}
        onMainPhotoChange={handleMainPhotoChange}
        username={formData.username || 'Usuário'}
      />

      {/* Bio Edit Modal */}
      <BioEditModal
        isOpen={isBioModalOpen}
        onClose={() => setIsBioModalOpen(false)}
        onSave={handleBioSave}
        initialBio={formData.bio || ''}
        occupation={formData.occupation}
        education={formData.education}
      />
    </div>
  );
};

export default ProfileEdit;